import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { apiRequest } from "@/lib/queryClient";
import { RefreshCcw } from "lucide-react";

const inviteTypeOptions = [
  { value: "all", label: "All Types" },
  { value: "competition", label: "Competition Invites" },
  { value: "challenge", label: "Challenge Invites" },
  { value: "general", label: "General Invites" },
];

const statusOptions = [
  { value: "all", label: "Any Status" },
  { value: "sent", label: "Sent" },
  { value: "delivered", label: "Delivered" },
  { value: "failed", label: "Failed" },
];

interface InviteLog {
  id: number;
  recipient: string;
  message: string;
  status: string | null;
  reference: string | null;
  templateId: number | null;
  sentAt: string | null;
  inviteType: string;
  contextType: string | null;
  contextId: number | null;
  contextName: string | null;
  friendId: number | null;
  friendName: string | null;
  friendPhone: string | null;
  channel?: string | null;
}

interface InviteLogResponse {
  success: boolean;
  data: InviteLog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  summary?: {
    total: number;
    smsCreditsUsed: number;
    emailInvites: number;
  };
}

export default function SMSInviteTrackingTab() {
  const [filters, setFilters] = useState({
    type: "all",
    status: "",
    recipient: "",
    friendId: "",
    contextId: "",
    startDate: "",
    endDate: "",
  });
  const [page, setPage] = useState(1);
  const limit = 25;

  const updateFilters = (updates: Partial<typeof filters>) => {
    setFilters((prev) => ({ ...prev, ...updates }));
    setPage(1);
  };

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        params.set(key, value);
      }
    });
    params.set("page", page.toString());
    params.set("limit", limit.toString());
    return params.toString();
  }, [filters, page]);

  const { data, isLoading, refetch, isFetching } = useQuery<InviteLogResponse>({
    queryKey: ["/api/sms-config/logs/invites", queryString],
    queryFn: async () => {
      const url = queryString
        ? `/api/sms-config/logs/invites?${queryString}`
        : "/api/sms-config/logs/invites";
      const response = await apiRequest("GET", url);
      return response.json();
    },
  });

  const logs = data?.data ?? [];
  const summary = data?.summary;

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <CardTitle>SMS Invite Tracking</CardTitle>
          <CardDescription>Monitor SMS invites sent for competitions and challenges.</CardDescription>
        </div>
        <Button variant="outline" onClick={() => refetch()} disabled={isFetching || isLoading}>
          <RefreshCcw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Select
            value={filters.type}
            onValueChange={(value) => updateFilters({ type: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Invite Type" />
            </SelectTrigger>
            <SelectContent>
              {inviteTypeOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.status || "all"}
            onValueChange={(value) =>
              updateFilters({ status: value === "all" ? "" : value })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Input
            placeholder="Recipient phone"
            value={filters.recipient}
            onChange={(event) => updateFilters({ recipient: event.target.value })}
          />

          <Input
            placeholder="Friend ID"
            value={filters.friendId}
            onChange={(event) => updateFilters({ friendId: event.target.value })}
          />

          <Input
            placeholder="Competition/Challenge ID"
            value={filters.contextId}
            onChange={(event) => updateFilters({ contextId: event.target.value })}
          />

          <Input
            type="date"
            value={filters.startDate}
            onChange={(event) => updateFilters({ startDate: event.target.value })}
          />

          <Input
            type="date"
            value={filters.endDate}
            onChange={(event) => updateFilters({ endDate: event.target.value })}
          />

          <Button
            variant="secondary"
            onClick={() =>
              updateFilters({
                type: "all",
                status: "",
                recipient: "",
                friendId: "",
                contextId: "",
                startDate: "",
                endDate: "",
              })
            }
          >
            Clear Filters
          </Button>
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          <div className="rounded-lg border p-4">
            <p className="text-xs uppercase text-muted-foreground">SMS Credits Used</p>
            <p className="mt-2 text-2xl font-semibold">
              {summary?.smsCreditsUsed ?? data?.pagination?.total ?? 0}
            </p>
          </div>
          <div className="rounded-lg border p-4">
            <p className="text-xs uppercase text-muted-foreground">Email Invites</p>
            <p className="mt-2 text-2xl font-semibold">{summary?.emailInvites ?? 0}</p>
          </div>
          <div className="rounded-lg border p-4">
            <p className="text-xs uppercase text-muted-foreground">Total Invites (Filtered)</p>
            <p className="mt-2 text-2xl font-semibold">
              {summary?.total ?? data?.pagination?.total ?? logs.length}
            </p>
          </div>
        </div>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Recipient</TableHead>
                <TableHead>Channel</TableHead>
                <TableHead>Invite Type</TableHead>
                <TableHead>Context</TableHead>
                <TableHead>Friend</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Sent At</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center">
                    Loading invite logs...
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center">
                    No SMS invite logs found for the selected filters.
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <div className="font-medium">{log.recipient}</div>
                      {log.friendPhone && (
                        <div className="text-xs text-muted-foreground">Friend phone: {log.friendPhone}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {(log as any).channel || "sms"}
                      </Badge>
                    </TableCell>
                    <TableCell className="capitalize">
                      <Badge variant="outline">{log.inviteType || "unknown"}</Badge>
                    </TableCell>
                    <TableCell>
                      {log.contextName ? (
                        <div>
                          <div className="font-medium">{log.contextName}</div>
                          <div className="text-xs text-muted-foreground">
                            {log.contextType ?? "context"} #{log.contextId ?? "n/a"}
                          </div>
                        </div>
                      ) : log.contextId ? (
                        <div>
                          ID: {log.contextId}
                          <div className="text-xs text-muted-foreground">Type: {log.contextType ?? "unknown"}</div>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">General</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {log.friendName ? (
                        <div>
                          <div className="font-medium">{log.friendName}</div>
                          {log.friendId && (
                            <div className="text-xs text-muted-foreground">Friend ID: {log.friendId}</div>
                          )}
                        </div>
                      ) : log.friendId ? (
                        <span className="text-muted-foreground text-sm">Friend #{log.friendId}</span>
                      ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={log.status === "failed" ? "destructive" : "secondary"}>
                        {log.status || "unknown"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {log.sentAt ? new Date(log.sentAt).toLocaleString() : "N/A"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {data?.pagination && (
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between text-sm text-muted-foreground">
            <div>
              Showing {(data.pagination.page - 1) * limit + 1}-
              {Math.min(data.pagination.page * limit, data.pagination.total)} of {data.pagination.total} invites
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={data.pagination.page === 1}
              >
                Previous
              </Button>
              <span>
                Page {data.pagination.page} of {Math.max(data.pagination.pages, 1)}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((prev) => Math.min(data.pagination.pages, prev + 1))}
                disabled={data.pagination.page >= data.pagination.pages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
